// Shared, sanitized Markdown. Copy controls are part of the rendered markup,
// not post-render DOM injection that can disappear during streaming.
import { marked } from "marked";
import DOMPurify from "dompurify";
import { useEffect, useMemo, useRef, useState } from "react";
import { copyTextToClipboard } from "../lib/clipboard";
import TableSheet, { readTable, type TableData } from "./TableSheet";
import "../styles/table-sheet.css";
import "../styles/reading.css";
import "../styles/ops-build-polish.css";

marked.setOptions({ gfm: true, breaks: true });
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.nodeName === "A" && node instanceof HTMLAnchorElement) {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});
function escapeHtml(text: string) {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
const bar = (label: string) => '<div class="md-code__bar"><span class="md-code__label">' + escapeHtml(label)
  + '</span><button type="button" class="md-code__copy" aria-label="Copy this block">Copy</button></div>';
const renderer = new marked.Renderer();
renderer.code = ({ text, lang }) => {
  const language = (lang ?? "").trim().split(/\s+/)[0] ?? "";
  // WhatsApp drafts (Julian, 8 Oct 2026; same as Build): a fenced `whatsapp` block is a message
  // ready to paste — readable wrapped text (not code font) with the same one-tap Copy.
  if (/^(whatsapp|wa)$/i.test(language)) {
    return '<div class="md-code md-wa">' + bar("WhatsApp message") + '<div class="md-code__body md-copy__content md-wa__text">'
      + escapeHtml(text.replace(/\n+$/, "")) + '</div></div>';
  }
  return '<div class="md-code">' + bar(language || "Text") + '<div class="md-code__body md-copy__content"><pre class="'
    + (language ? "" : "md-pre--wrap") + '"><code>' + escapeHtml(text) + '</code></pre></div></div>';
};
renderer.blockquote = function ({ tokens }) {
  return '<div class="md-code md-copyQuote">' + bar("Message") + '<blockquote class="md-copy__content">'
    + this.parser.parse(tokens) + '</blockquote></div>';
};
// Tables (29 Sep 2026): numeric columns are marked for right alignment, every cell carries its
// column's label, and the wrapper says how many columns there are. A tap on the table (or its
// expand control) opens it in the table sheet (TableSheet.tsx).
const NUMERIC_CELL = /^[\s(]*[-+−~≈]?\s*(?:S?\$|€|£)?\s*\d[\d,.]*\s*(?:%|x|×|k)?[\s)]*$/i;
renderer.table = function (token) {
  const cols = token.header.length;
  const numeric = token.header.map((_, c) => token.rows.length > 0 &&
    token.rows.every((row) => NUMERIC_CELL.test(row[c]?.text ?? "") || !(row[c]?.text ?? "").trim()));
  const label = token.header.map((cell) => escapeHtml((cell.text ?? "").replace(/[*_`]/g, "").trim()));
  const attrs = (c: number, align: string | null) => (numeric[c] ? ' class="is-num"' : "")
    + (align ? ' align="' + align + '"' : "");
  const head = '<thead><tr>' + token.header.map((cell, c) =>
    '<th' + attrs(c, cell.align) + '>' + this.parser.parseInline(cell.tokens) + '</th>').join("") + '</tr></thead>';
  const body = token.rows.length ? '<tbody>' + token.rows.map((row) => '<tr>' + row.map((cell, c) =>
    '<td data-label="' + (label[c] ?? "") + '"' + attrs(c, cell.align) + '>' + this.parser.parseInline(cell.tokens) + '</td>').join("")
    + '</tr>').join("") + '</tbody>' : "";
  // The open control sits in the header row's top-right corner, inside the table's frame
  // (Build, 8 Oct 2026); the last header cell keeps clear of it (styles/ops-build-polish.css).
  // Lucide "maximize-2" as inline SVG (the reply markup is a string, not React).
  const open = token.rows.length ? '<button type="button" class="md-table__open" aria-label="Open table">'
    + '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
    + '<polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line></svg></button>' : '';
  return '<div class="md-tableBox' + (open ? ' has-open' : '') + '"><div class="md-table-wrap" data-cols="' + cols
    + '" role="region" aria-label="Scrollable table" tabindex="0"><table>' + head + body + '</table></div>' + open + '</div>';
};
// Your own messages (ported from Build, 9 Oct 2026): the same Markdown, read-only and stricter.
// Raw HTML you type or paste is shown as text (never parsed), images stay links, and code
// blocks have no copy bar.
const userRenderer = new marked.Renderer();
userRenderer.html = ({ text }) => escapeHtml(text);
userRenderer.image = ({ href, text }) => escapeHtml(text || href);
userRenderer.code = ({ text }) => '<pre class="md-pre--wrap"><code>' + escapeHtml(text) + '</code></pre>';
export function UserMarkdown({ text, className }: { text: string; className?: string }) {
  const markup = useMemo(() => ({ __html: DOMPurify.sanitize(marked.parse(text, { async: false, renderer: userRenderer }) as string, {
    ADD_ATTR: ["target", "rel"], FORBID_TAGS: ["img", "input", "style", "form"],
  }) }), [text]);
  return <div className={`md md--user ${className ?? ""}`} dangerouslySetInnerHTML={markup} />;
}
interface Props { text: string; className?: string }
export default function Markdown({ text, className }: Props) {
  const timers = useRef(new Map<HTMLElement, number>());
  const html = useMemo(() => DOMPurify.sanitize(marked.parse(text, { async: false, renderer }) as string, {
    ADD_ATTR: ["target", "rel", "type", "aria-label", "tabindex"],
  }), [text]);
  // Keep identical rendered text nodes intact during queue/status updates.
  // Replacing innerHTML for an unchanged reply clears the reader's selection.
  const markup = useMemo(() => ({ __html: html }), [html]);
  const [reader, setReader] = useState<TableData | null>(null);
  // Wide tables: a right-edge fade while there is more to the right (is-scrollable and not
  // is-scrolled-end on .md-tableBox; styles/ops-build-polish.css). Classes only — the markup is untouched.
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const boxes = Array.from(root.querySelectorAll<HTMLElement>(".md-tableBox"));
    if (!boxes.length) return;
    const update = (box: HTMLElement) => {
      const wrap = box.querySelector<HTMLElement>(".md-table-wrap");
      if (!wrap) return;
      const scrollable = wrap.scrollWidth > wrap.clientWidth + 1;
      box.classList.toggle("is-scrollable", scrollable);
      box.classList.toggle("is-scrolled-end", !scrollable || wrap.scrollLeft + wrap.clientWidth >= wrap.scrollWidth - 2);
    };
    const observer = typeof ResizeObserver === "function"
      ? new ResizeObserver((entries) => entries.forEach((entry) => {
          const box = (entry.target as HTMLElement).closest<HTMLElement>(".md-tableBox");
          if (box) update(box);
        }))
      : null;
    const offs = boxes.map((box) => {
      const wrap = box.querySelector<HTMLElement>(".md-table-wrap");
      const onScroll = () => update(box);
      wrap?.addEventListener("scroll", onScroll, { passive: true });
      if (wrap) observer?.observe(wrap);
      const table = wrap?.querySelector("table");
      if (table) observer?.observe(table);
      update(box);
      return () => wrap?.removeEventListener("scroll", onScroll);
    });
    return () => { offs.forEach((off) => off()); observer?.disconnect(); };
  }, [html]);
  // Declared after the table effect on purpose: the copy-control unit test keeps the last cleanup.
  useEffect(() => () => {
    for (const timer of timers.current.values()) window.clearTimeout(timer);
    timers.current.clear();
  }, []);
  function onClick(event: React.MouseEvent<HTMLDivElement>) {
    // The expand control, or a tap anywhere on a table that isn't a link, opens the table sheet.
    const target = event.target as HTMLElement;
    const box = target.closest<HTMLElement>(".md-tableBox");
    if (box && (target.closest(".md-table__open") || !target.closest("a, button, code"))
      && !window.getSelection?.()?.toString()) {
      const data = readTable(box);
      if (data?.rows.length) {
        event.preventDefault();
        event.stopPropagation();
        setReader(data);
        return;
      }
    }
    const button = (event.target as HTMLElement).closest<HTMLElement>(".md-code__copy");
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    const content = button.closest(".md-code")?.querySelector(".md-copy__content");
    if (!content) return;
    const source = content.cloneNode(true) as HTMLElement;
    source.querySelectorAll(".md-code__bar").forEach(node => node.remove());
    const isQuote = content.tagName === "BLOCKQUOTE";
    if (isQuote) {
      source.querySelectorAll("br").forEach(node => node.replaceWith("\n"));
      source.querySelectorAll("p,li").forEach(node => node.append("\n"));
    }
    const plainText = source.textContent ?? "";
    void copyTextToClipboard(isQuote ? plainText.trimEnd() : plainText).then(ok => {
      if (!button.isConnected) return;
      const previous = timers.current.get(button);
      if (previous) window.clearTimeout(previous);
      button.textContent = ok ? "Copied" : "Try again";
      button.classList.toggle("is-copied", ok);
      button.setAttribute("aria-label", ok ? "Block copied" : "Copy failed. Try again");
      timers.current.set(button, window.setTimeout(() => {
        button.textContent = "Copy";
        button.classList.remove("is-copied");
        button.setAttribute("aria-label", "Copy this block");
        timers.current.delete(button);
      }, 1800));
    });
  }
  return <>
    <div ref={rootRef} className={`md ${className ?? ""}`} onClick={onClick} dangerouslySetInnerHTML={markup} />
    {reader && <TableSheet data={reader} onClose={() => setReader(null)} />}
  </>;
}
